const secondsInHour = 3600;
const secondsInDay = secondsInHour * 24;
const secondsInWeek = secondsInDay * 7;

/**
 * UI period options used by simple-query inputs when converting
 * backend "relative offset" values (seconds/days) into a stable UI selection.
 *
 * Note: In our query-builder UI, "months" are treated as 30-day buckets for
 * relative offsets to match backend behavior.
 */
export type RelativeOffsetPeriod = 'days' | 'weeks' | 'months';

export const getRatioForPeriod = (period: 'day' | 'week' | 'hour') => {
  if (period === 'week') {
    return secondsInWeek;
  }
  if (period === 'day') {
    return secondsInDay;
  }
  if (period === 'hour') {
    return secondsInHour;
  }
};

export const convertSecondsToPeriodAndUnit = (seconds: number): { period: 'day' | 'week' | 'hour'; units: number } => {
  if (!seconds) {
    return { units: null, period: 'day' };
  }

  // Test in decending order, finding the first largest
  // option that does not have a remainder
  if (seconds % secondsInWeek === 0) {
    return { period: 'week', units: seconds / secondsInWeek };
  }

  if (seconds % secondsInDay === 0) {
    return { period: 'day', units: seconds / secondsInDay };
  }

  if (seconds % secondsInHour === 0) {
    return { period: 'hour', units: seconds / secondsInHour };
  }
};

// Backend sends seconds; UI works in days/weeks/months.
export const inferUiOffsetFromBackendSeconds = (
  seconds: number
): { units: number; period: RelativeOffsetPeriod } => {
  const days = Math.trunc(seconds / secondsInDay);
  return inferUiOffsetFromBackendDays(days);
};

// Reverse of the "UI -> backend days" mapping. Prefer the coarsest unit that round-trips cleanly.
export const inferUiOffsetFromBackendDays = (days: number): { units: number; period: RelativeOffsetPeriod } => {
  if (days % 30 === 0) {
    return { units: Math.max(1, Math.trunc(days / 30)), period: 'months' };
  }
  if (days % 7 === 0) {
    return { units: Math.max(1, Math.trunc(days / 7)), period: 'weeks' };
  }
  return { units: Math.max(1, Math.trunc(days)), period: 'days' };
};
